<div class="flex h-full w-full flex-col">
  <!-- Messages area (chat view) -->
  <div class="mt-0.5 flex h-2 w-full flex-grow flex-row">
    <div class="flex w-2 flex-1 flex-col overflow-y-auto">
      <m-session-messages class="h-full w-full" [scrollTrigger]="scrollTrigger">
        <m-session-messages-content
          [turns]="turns"
          [session]="session"
          [isActivating]="isActivating"
          [isArchived]="isArchived"
          [isSessionBusy]="isSessionBusy"
          [retryMessage]="retryMessage"
          [isSessionError]="isSessionError"
          [lastSessionError]="lastSessionError"
          [isLastErrorRecovered]="isLastErrorRecovered"
        ></m-session-messages-content>
      </m-session-messages>
    </div>
  </div>

  <!-- Working indicator -->
  <div
    class="flex h-[30px] flex-col justify-center"
    [ngClass]="{
      'ml-[50px]': session?.type === sessionTypeEnum.Explorer,
      'ml-[105px]': session?.type !== sessionTypeEnum.Explorer
    }"
  >
    <div
      *ngIf="isWorking"
      class="relative h-9 w-9"
      [tp]="retryMessage"
      [tpIsEnabled]="!!retryMessage"
    >
      <ngx-spinner
        [name]="workingSpinnerName"
        [color]="workingSpinnerColor"
        bdColor="#00000000"
        size="default"
        type="ball-pulse"
        [fullScreen]="false"
        [disableAnimation]="true"
        [zIndex]="10"
      ></ngx-spinner>
    </div>
  </div>

  <!-- Permission dock -->
  <div
    *ngIf="
      session?.type !== sessionTypeEnum.Explorer &&
      permissions.length > 0 &&
      !isArchived &&
      session.sessionId &&
      !permissionsAutoAcceptSessionIds.includes(session.sessionId)
    "
    class="mx-auto mb-8 flex w-full max-w-[976px] flex-shrink-0 flex-col gap-2 px-6 pb-2 pt-2"
  >
    <div class="border-s300 flex flex-col rounded-lg border bg-white">
      <div class="px-5 py-4">
        <div class="text-s500 mb-1 text-[15px] font-normal">
          Permission request
        </div>
        <div class="text-s1 text-lg font-medium">
          {{ permissions[0].permission }}
        </div>
        <div
          *ngIf="permissions[0].patterns?.length > 0"
          class="text-s500 mt-1 text-base font-normal"
        >
          {{ permissions[0].patterns.join(', ') }}
        </div>
      </div>
      <div
        class="border-s200 flex items-center justify-end gap-3 border-t px-5 py-3"
      >
        <button
          type="button"
          class="text-s500 hover:text-s1 rounded px-4 py-2 text-[17px] focus:outline-none"
          (click)="
            respondToPermission({
              permissionId: permissions[0].id,
              reply: 'reject'
            })
          "
        >
          Deny
        </button>
        <button
          type="button"
          class="border-m600 text-m600 rounded border px-4 py-2 text-[17px] hover:opacity-90 focus:outline-none"
          (click)="
            respondToPermission({
              permissionId: permissions[0].id,
              reply: 'once'
            })
          "
        >
          Allow Once
        </button>
        <button
          type="button"
          class="bg-m600 rounded px-4 py-2 text-[17px] text-white hover:opacity-90 focus:outline-none"
          (click)="
            respondToPermission({
              permissionId: permissions[0].id,
              reply: 'always'
            })
          "
        >
          Allow Always
        </button>
      </div>
    </div>
  </div>

  <!-- Question dock -->
  <div
    *ngIf="
      session?.type !== sessionTypeEnum.Explorer &&
      questions.length > 0 &&
      !isArchived
    "
    class="mx-auto mb-8 flex w-full max-w-[976px] flex-shrink-0 flex-col gap-2 px-6 pb-2 pt-2"
  >
    <m-question-prompt
      [question]="questions[0]"
      (answer)="
        respondToQuestion({ questionId: questions[0].id, answers: $event })
      "
      (reject)="rejectQuestion({ questionId: questions[0].id })"
    ></m-question-prompt>
  </div>

  <!-- Archived session message -->
  <div *ngIf="isArchived" class="flex-shrink-0 px-6 pb-4 pt-[6px]">
    <div class="mx-auto flex max-w-[860px] items-center justify-center">
      <div class="text-s400 py-1 text-lg">{{
        archiveReason === archiveReasonEnum.Expire
          ? 'Session is archived (sandbox expired)'
          : archiveReason === archiveReasonEnum.Commit
            ? 'Session is archived (commit by User)'
            : 'Session is archived by User'
      }}</div>
    </div>
  </div>

  <!-- Paused session message -->
  <div
    *ngIf="session?.status === sessionStatusEnum.Paused && !isOptimisticLoading"
    class="flex-shrink-0 px-6 pb-4 pt-[6px]"
  >
    <div class="mx-auto flex max-w-[860px] items-center justify-center">
      <div class="text-s400 py-1 text-lg">{{
        pauseReason === pauseReasonEnum.User
          ? 'Session is paused by User'
          : pauseReason === pauseReasonEnum.Idle
            ? 'Session is paused (idle)'
            : pauseReason === pauseReasonEnum.Safe
              ? 'Session is paused (safe)'
              : pauseReason === pauseReasonEnum.External
                ? 'Session is paused (external)'
                : 'Session is paused'
      }}</div>
    </div>
  </div>

  <!-- Input area (pinned to bottom, hidden when docks are active) -->
  <div
    *ngIf="
      (permissions.length === 0 ||
        (session.sessionId &&
          permissionsAutoAcceptSessionIds.includes(session.sessionId))) &&
      questions.length === 0 &&
      !isArchived
    "
    class="relative flex-shrink-0 px-6 pb-8 pt-[6px]"
  >
    <div class="mx-auto max-w-[860px]">
      <div class="flex items-start">
        <div class="mr-3 w-3 flex-shrink-0"></div>
        <div class="min-w-0 flex-1" [class.invisible]="!showSessionInput">
          <m-session-input
            [(agent)]="agent"
            [(modelExtraId)]="modelExtraId"
            [(variant)]="variant"
            [sessionType]="session?.type"
            [disabled]="isActivating || isSessionError"
            [isWorking]="isWorking"
            [showSelects]="messages.length > 0"
            [sessionId]="session?.sessionId"
            (send)="sendFollowUp($event)"
            (stop)="stopSession()"
          ></m-session-input>
        </div>
        <div class="ml-3 w-3 flex-shrink-0"></div>
      </div>
    </div>
  </div>
</div>
<div *ngIf="permissionsAutoAcceptSessionIds$ | async"></div>
<div *ngIf="sessionAndData$ | async"></div>
<div *ngIf="liveEvents$ | async"></div>
<div *ngIf="isOptimisticLoading$ | async"></div>
<div *ngIf="showSessionInput$ | async"></div>
